import { portalIsProduction } from "@gymflow/helpers";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createBrowserHistory } from "history";
import { queryClient } from "libs/api/src/lib/apiV2";
import { MotionConfig } from "motion/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useMemo } from "react";
import {
  Redirect,
  Route,
  Router,
  Switch,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { Toaster } from "sonner";

import { useEmbedLoadingStyleStart } from "./hooks/useEmbedLoadingStyle";
import { PortalRoutesContext } from "./hooks/usePortalRoutes";
import { Embed } from "./layouts/Embed";
import KioskLayout from "./layouts/Kiosk";
import { LinkLayout } from "./layouts/Link";
import { Migration as MigrationLayout } from "./layouts/Migration";
import { Site } from "./layouts/Site";
import StaffLayout from "./layouts/Staff";
import { portalEnv } from "./portalEnv";
import { ApiResolverProvider } from "./providers";
import { ErrorParserProvider } from "./providers/ErrorParserProvider";
import { ToastProvider } from "./providers/ToastProvider";
import { RouteFeature } from "./routes/feature";
import { RouteLayout } from "./routes/layout";
import { GenericSecurePayment } from "./views/GenericSecurePayment";
import { GenericSecurePaymentMethod } from "./views/GenericSecurePaymentMethod";
import { StripeRedirect } from "./views/StripeRedirect";
import { Welcome } from "./views/Welcome";

const hist = createBrowserHistory();

function PortalRoutes() {
  const match = useRouteMatch();
  useEmbedLoadingStyleStart();

  return (
    <Switch>
      <Route path={`${match.path}${RouteLayout.Staff}`}>
        {() => <StaffLayout />}
      </Route>
      <Redirect
        path={`${match.path}${RouteLayout.Member}`}
        to={`${match.path}${RouteLayout.Site}${RouteFeature.SiteProfile}`}
      />
      <Route path={`${match.path}${RouteLayout.Kiosk}`}>
        {(props) => <KioskLayout {...props} />}
      </Route>
      <Route path={`${match.path}${RouteLayout.Site}`}>{() => <Site />}</Route>
      <Route path={`${match.path}${RouteLayout.Migration}`}>
        <MigrationLayout />
      </Route>
      <Route path={`${match.path}${RouteLayout.Embed}`}>
        {() => <Embed />}
      </Route>
      <Route path={`${match.path}${RouteLayout.Link}`}>
        {(props) => <LinkLayout {...props} />}
      </Route>
      <Route>
        <Redirect
          exact
          from={match.path}
          to={`${match.url}${RouteLayout.Staff}`}
        />
      </Route>
    </Switch>
  );
}

function PortalBootstrap() {
  const { brand, company, location } = useParams<{
    brand: string;
    company: string;
    location: string;
  }>();

  const routesConfig = useMemo(
    () => ({
      routeId: `${brand}/${company}/${location}`,
      brand,
      company,
      location,
    }),
    [brand, company, location],
  );

  return (
    <PortalRoutesContext.Provider value={routesConfig}>
      <PortalRoutes />
    </PortalRoutesContext.Provider>
  );
}

if (portalIsProduction()) {
  posthog.init("phc_MNP3DHE0ICvVTOFn07UKGPyLfGUjncfNgq1Drai9g1u", {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
  });
}

function PortalRoot() {
  return (
    <PostHogProvider client={posthog}>
      <MotionConfig reducedMotion="user">
        <QueryClientProvider client={queryClient}>
          <Toaster position="top-center" className="w-96" />
          <ToastProvider>
            <ErrorParserProvider>
              <Router history={hist}>
                <Switch>
                  <Route path={`/stripe-redirect`}>
                    <StripeRedirect />
                  </Route>
                  <Route path="/:brand/:company/:location">
                    <ApiResolverProvider>
                      <PortalBootstrap />
                    </ApiResolverProvider>
                  </Route>
                  <Route path={RouteFeature.SiteSecurePayment}>
                    <GenericSecurePayment />
                  </Route>
                  <Route path={RouteFeature.SiteSecurePaymentMethod}>
                    <GenericSecurePaymentMethod />
                  </Route>
                  <Route path="">{() => <Welcome />}</Route>
                </Switch>
              </Router>
            </ErrorParserProvider>
          </ToastProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </MotionConfig>
    </PostHogProvider>
  );
}

export default portalEnv.SENTRY_ENABLED
  ? Sentry.withProfiler(PortalRoot)
  : PortalRoot;
